import { moodColor, moodLabel } from '@adhd/shared';
import type { MoodCheckin } from '@adhd/shared';
import { useState } from 'react';
import { TrashIcon } from './Icons.js';
import { api } from '../lib/api.js';
import { formatTime, whenWritten, writtenLate } from '../lib/mood.js';

/**
 * How far back a check-in can be removed: today only.
 *
 * Exported and shared because the same rule is enforced on the server
 * (routes/mood.ts, the DELETE handler) and the two must not drift. This copy
 * decides whether to draw the bin; the server copy is the one that counts.
 * Offering a control the server refuses is worse than not offering it.
 *
 * A past day is only half editable. Removing a check-in recomputes the day's
 * figures live, everywhere, including inside a share link. The day's written
 * account does not — a settled day's sentences are frozen deliberately. So a
 * past day edited here would show new figures under sentences describing the
 * old ones, and the only ways out are rewriting a finished day's account or
 * deleting it. Both cost more than the correction is worth.
 */
export function removableOn(day: string, today: string): boolean {
  return day === today;
}

/**
 * One check-in, wherever it is listed.
 *
 * One component rather than two, because there were two: Today and the Log had
 * near-identical card markup and only one of them could delete anything. This
 * file's neighbours carry the same warning — two implementations of the same
 * card drift within a week, and this app's recurring defect is the app
 * disagreeing with itself about the same fact on two screens.
 *
 * The card owns `asking` rather than the bin, because the two halves of the
 * question live in different places: the bin is small and in the corner, the
 * confirmation is full width. Held inside the bin, the question rendered inside
 * the header row at 62% of the card width, wrapped beside the timestamp — the
 * shape of a confirmation you agree to without reading.
 */
export function CheckinCard({
  c,
  today,
  onGone,
  label,
}: {
  c: MoodCheckin;
  /** Today where the reader is. Omit to draw no bin at all. */
  today?: string;
  onGone?: () => void;
  /** Said under the mood when the card is hanging off something, so the rail is
      not the only thing explaining why it is there. */
  label?: string;
}): JSX.Element {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // The same rule as a journal in the day feed: a check-in filed under a day
  // other than the one it was made on says so. One made at 1:05am from the
  // journal of the night before sits under that night — and read "1:05 AM"
  // there, which is that day's small hours, a time it was not.
  const at = writtenLate(c.day, c.recordedAt) ? whenWritten(c.recordedAt) : formatTime(c.recordedAt);
  const removable = Boolean(today && onGone && removableOn(c.day, today));

  return (
    <div className="card">
      <div className="row-between">
        <strong style={{ color: moodColor(c.mood) }}>{moodLabel(c.mood)}</strong>
        <span className="checkin-head-right">
          <span className="faint">{at}</span>
          {/*
            Top right beside the time, rather than a worded link at the foot. A
            record of how somebody felt should not carry the word "Remove" under
            every entry — the common case by far is reading it. Faint until
            hovered, and it only takes the danger colour then.
          */}
          {removable && !asking && (
            <button
              className="checkin-bin"
              onClick={() => setAsking(true)}
              aria-label={`Remove the ${at} check-in`}
              title="Remove"
            >
              <TrashIcon />
            </button>
          )}
        </span>
      </div>
      {label && (
        <p className="faint" style={{ margin: 0 }}>
          {label}
        </p>
      )}
      {c.contextTags.length > 0 && (
        <div className="chips" style={{ marginTop: 'var(--space-2)' }}>
          {c.contextTags.map((t) => (
            <span className="chip" key={t} style={{ cursor: 'default' }}>
              {t}
            </span>
          ))}
        </div>
      )}
      {c.note && (
        <p className="muted" style={{ marginTop: 'var(--space-2)' }}>
          {c.note}
        </p>
      )}
      {asking && (
        <div className="checkin-remove stack-sm stack">
          <span className="faint">Remove the {at} check-in? It does not come back.</span>
          <div className="chips">
            <button
              className="btn btn-secondary btn-compact"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setFailed(null);
                void api
                  .deleteCheckin(c.id)
                  .then(() => onGone?.())
                  .catch((e: unknown) => {
                    // The server's own sentence when it refuses, so a window
                    // that has closed says so rather than reading as a fault.
                    setFailed(e instanceof Error ? e.message : 'That did not work. Still there.');
                    setBusy(false);
                  });
              }}
            >
              {busy ? 'Removing…' : 'Yes, remove it'}
            </button>
            <button className="btn btn-ghost btn-compact" onClick={() => setAsking(false)}>
              Keep it
            </button>
          </div>
          {failed && <span className="faint">{failed}</span>}
        </div>
      )}
    </div>
  );
}
